import type { Boss, SpriteKey } from './types'

// 崩れから構えへ立ち上がる2枚 (2026-10-30・作者の答え docs/research/2026-10-29-normal-execute-answers-2.md):
//   「崩れている絵から立ち上がって、そのアイドルの絵になる前に立ち上がるモーションの絵を入れてほしい。
//    今だと倒れてたのにスクッと、なんかパッといきなり立ち上がってるんだよね」。
//   致命の後 (崩れ姿のまま抜かれた後) と、体幹の崩れが時間切れで戻る時の両方で、崩れ → rise1 → rise2 → 構え。
//   絵は描画だけで出す。待機のうちだけ描き、技の予兆へ移ったり動き出したらすぐやめる。
//   崩れが解けた最初のコマでボスは歩き出すので、絵のあるボスだけ、立ち上がる間は足を止める (riseHoldMs・boss.ts)。
//   致命の後は崩れ姿を見せる間 (LAMP_EXEC.bossDownAfterHitMs) も足を止める (崩れ姿のまま床を滑らないように)。
//   絵 = public/sprites/bosses/<boss>/rise1・rise2 (介錯は blade/・cannon/ の下)。絵の無いボスは今まで通り。
//   作り方 = Codex の原画 (そざい/concept/boss-rise-1030/) を scripts/boss_rise_cut.py で切り抜き・構えの倍率へ縮める。
//   比べる時は ?bossRise=off (前の、崩れからすぐ構え)。
export const BOSS_RISE_STEP_MS = 150
const STEPS: readonly SpriteKey[] = ['rise1', 'rise2']

// 切り替えを滑らかにする (2026-10-30 作者「切り替わりの滑らかさがない」→ 答え both「自然さが出た」)。
//   記録 = docs/research/2026-10-30-boss-rise-smooth.md・2026-10-30-boss-rise-both.md。
//   指定なし (配布版) = 'both'。比べる時は ?bossRiseSmooth=off (前の、各段 150ms のハードカット)・ease・blend。
//   'ease'  = 段の時間を重い出だし→速い伸び上がり (190ms → 110ms・合計は同じ 300ms) にし、
//             各段の中で体を足元から少しずつ伸ばす (縦 0.97 → 1)。構えに入った所で一度だけ伸び切って戻る (1.02 → 1・160ms)。
//   'blend' = 3か所の切り替え (崩れ→rise1→rise2→構え) だけ、次の絵の頭 70ms で前の絵を溶かして消す。全体の重ね (BOSS_SPRITE_CROSSFADE) は戻さない。
//   'both'  = ease に、形の近い2か所 (崩れ→rise1・rise2→構え) だけの溶け替わりを足す。
//             形の大きく違う rise1→rise2 は溶かすと2体が重なって見える (10-30 撮影で花嫁・騎士に残影) ので、そこはハードカット。
export type BossRiseSmooth = 'off' | 'ease' | 'blend' | 'both'
let smooth: BossRiseSmooth = 'both'
export function setBossRiseSmooth(raw: string | null | undefined): void {
  smooth = raw === 'off' || raw === 'ease' || raw === 'blend' ? raw : 'both'
}
export function bossRiseSmooth(): BossRiseSmooth { return smooth }

// 立ち上がりの絵の大きさ・高さの合わせ (10-30・src/sprites.ts BOSS_RISE_SCALE・亡霊は render.ts shadeBobOffset)。比べる時は ?bossRiseFit=off。
let fitOn = true
export function setBossRiseFit(raw: string | null | undefined): void { fitOn = raw !== 'off' }
export function bossRiseFitOn(): boolean { return fitOn }
const EASE_STEP_MS: readonly number[] = [190, 110]
export const BOSS_RISE_BLEND_MS = 70
const SETTLE_MS = 160
function stepMs(): readonly number[] {
  return smooth === 'ease' || smooth === 'both' ? EASE_STEP_MS : [BOSS_RISE_STEP_MS, BOSS_RISE_STEP_MS]
}

/** 立ち上がりの絵があるボス (10-30 騎士と王 → 作者 go で亡霊・花嫁・介錯・覚醒獣へ。月喰いの王は対象外)。 */
const RISE_BOSS_IDS: ReadonlySet<string> = new Set([
  'vigil-knight', 'dream-hound', 'nameless-shade', 'bride', 'the-executioner', 'crowned-king-awoken',
])

let riseOn = true
export function setBossRise(raw: string | null | undefined): void { riseOn = raw !== 'off' }

/** 立ち上がる間、ボスの足を止める ms (絵の無いボス・?bossRise=off では 0)。extraMs = その前に崩れ姿を見せる間。 */
export function bossRiseHoldMs(bossId: string, extraMs = 0): number {
  return riseOn && RISE_BOSS_IDS.has(bossId) ? extraMs + BOSS_RISE_STEP_MS * STEPS.length : 0
}

let wasDown = false
let startSec: number | null = null
let startX = 0
let current: SpriteKey | null = null
// 何段目か (0 = rise1・1 = rise2・2 = 構えに入った後の余韻) と、その段に入ってからの ms。余韻の間は current = null。
let phase = -1
let phaseMs = 0

/** 毎フレーム描く前に1回。down = この絵の前のフレームまで崩れ姿を描いていたか (本物の崩れ・致命の後の崩れ姿)。 */
export function advanceBossRise(boss: Pick<Boss, 'state' | 'hp' | 'x'>, down: boolean, nowSec: number): void {
  if (!riseOn || boss.hp <= 0) { wasDown = down; stop(); return }
  if (down) { wasDown = true; stop(); return }
  if (wasDown && boss.state === 'idle') { startSec = nowSec; startX = boss.x }
  wasDown = false
  if (startSec === null || boss.state !== 'idle') { stop(); return }
  const ms = (nowSec - startSec) * 1000
  const steps = stepMs()
  const tailMs = smooth === 'off' ? 0 : Math.max(SETTLE_MS, BOSS_RISE_BLEND_MS)
  let at = 0
  phase = -1
  for (let i = 0; i <= steps.length; i++) {
    const len = i < steps.length ? steps[i]! : tailMs
    if (ms >= at && ms < at + len) { phase = i; phaseMs = ms - at; break }
    at += len
  }
  // 動き出したらやめる (構えに入った後の余韻は歩き出しても続ける = 溶け替わりと伸びを途中で切らない)。
  if (phase < 0 || (phase < STEPS.length && Math.abs(boss.x - startX) > 2)) { stop(); return }
  current = phase < STEPS.length ? STEPS[phase]! : null
}
function stop(): void { startSec = null; current = null; phase = -1; phaseMs = 0 }

/** いま描く立ち上がりの絵 (無ければ null)。 */
export function bossRiseKey(): SpriteKey | null { return current }

/** blend・both の溶け替わり: 次の絵の頭 BOSS_RISE_BLEND_MS だけ、下に描く前の絵 from と、上の絵の濃さの進み t (0→1)。
 *  from = 'staggered' は崩れ姿 (二段の崩れの絵があるボスは描く側で staggered_hold に読み替える)。無ければ null。 */
export function bossRiseBlend(): { from: SpriteKey; t: number } | null {
  if ((smooth !== 'blend' && smooth !== 'both') || phase < 0 || phaseMs >= BOSS_RISE_BLEND_MS) return null
  if (smooth === 'both' && phase === 1) return null
  const from: SpriteKey = phase === 0 ? 'staggered' : STEPS[phase - 1]!
  const u = phaseMs / BOSS_RISE_BLEND_MS
  return { from, t: u * u * (3 - 2 * u) }
}

/** 立ち上がりの間に縦へ掛ける倍率 (足元基準)。ease・both のほかは 1。 */
export function bossRiseScaleY(): number {
  if ((smooth !== 'ease' && smooth !== 'both') || phase < 0) return 1
  if (phase < STEPS.length) {
    const u = Math.min(1, phaseMs / stepMs()[phase]!)
    return 0.97 + 0.03 * (1 - (1 - u) * (1 - u))
  }
  const u = Math.min(1, phaseMs / SETTLE_MS)
  return 1 + 0.02 * Math.sin(Math.PI * u)
}

export function resetBossRise(): void { wasDown = false; stop() }
